import type { Timestamp } from 'firebase/firestore'

import type { RecordField } from '#survey/schemas/tuning'

import type { AgeRange } from './resolveDateRangeBounds'
import type { QuestDoc, RecordOverridesEntry } from './schemas'

export type EffectiveMute = {
  submissionId: string
  questId: string
  updatedAt: Timestamp
}

type EffectiveOverrideBase = {
  submissionId: string
  /** The quest whose change won, for attribution in the UI. */
  questId: string
  updatedAt: Timestamp
}

export type EffectiveMoneyOverride = EffectiveOverrideBase & {
  field: 'annualValue'
  value: number
  /** The parsed value the override was made against, when the writer stored it. */
  baseValue: number | null
}

export type EffectiveDateRangeOverride = EffectiveOverrideBase & {
  field: 'dateRange'
  value: AgeRange
  /** The plan's own window when the override was made, when the writer stored it. */
  baseValue: AgeRange | null
}

/**
 * Discriminated on `field`, because the two fields resolve to different things:
 * an amount is a number, a window is a pair of ages. A single `value: number`
 * would have forced a cast at every reader.
 */
export type EffectiveRecordOverride =
  EffectiveMoneyOverride | EffectiveDateRangeOverride

/** Which quest is in control of one plan record, and when it took control. */
export type RecordOwner = {
  submissionId: string
  questId: string
  updatedAt: Timestamp
}

export type EffectiveOverrides = {
  /** Keyed `${submissionId}:${field}`. Overrides on the parsed record. */
  recordOverrides: Map<string, EffectiveRecordOverride>
  /** Keyed submissionId. Present only when a mute is in effect. */
  mutes: Map<string, EffectiveMute>
  /** Keyed submissionId. Present for every record any quest has touched. */
  owners: Map<string, RecordOwner>
}

export const effectiveRecordOverrideKey = (
  submissionId: string,
  field: RecordField
): string => `${submissionId}:${field}`

const isNewer = (candidate: Timestamp, incumbent: Timestamp): boolean =>
  candidate.toMillis() > incumbent.toMillis()

/** When this quest last touched this record, across every field and the mute. */
const lastTouchedAt = (entry: RecordOverridesEntry): Timestamp | undefined => {
  const stamps = [
    entry.fields?.annualValue?.updatedAt,
    entry.fields?.dateRange?.updatedAt,
    entry.muted?.updatedAt,
  ].filter((stamp): stamp is Timestamp => stamp !== undefined)

  return stamps.reduce<Timestamp | undefined>(
    (newest, stamp) => (!newest || isNewer(stamp, newest) ? stamp : newest),
    undefined
  )
}

/**
 * Resolves what the quests collectively do to the plan's submissions, so the
 * projection and the UI can read one answer instead of each deriving its own.
 * That divergence is what let a quest's sheet show the plan baseline while the
 * projection was running another quest's value.
 *
 * The rule is most-recent-edit-wins per SUBMISSION, not per field: the quest that
 * touched a record last owns the whole record — every axis and the mute — and no
 * other quest's entry for it applies. An axis the owner has not set falls back to
 * the plan, not to the value some other quest put there.
 *
 * Per-field competition came first and let two quests split one record, which the
 * sheet then had to explain one control at a time: a notice and a takeover button
 * under each slider, each transferring one axis. A record has one owner now, so
 * there is one notice and one takeover, and a quest cannot end up half in control
 * of something.
 *
 * Muting rides on the same ownership rather than its own timeline. The guarantee
 * that a value nudge must not silently re-enable a record another quest switched
 * off is kept by the takeover action, which copies the mute along with the values
 * when it claims the record.
 *
 * Order-independent by construction: it compares timestamps, never list position,
 * so passing the quests in any order gives the same answer. That is the point —
 * the original behaviour resolved by list order, i.e. MMKV insertion order, i.e.
 * quest creation order, which no user could see or predict.
 *
 * Rollback falls out of it: remove the winning quest's entry and the next most
 * recent quest owns the record, rather than the plan baseline reappearing.
 *
 * Pass only the quests participating in the scenario — see `scenarioActiveQuests`.
 * Enablement is deliberately the caller's filter.
 */
const resolveEffectiveOverrides = (
  quests: readonly QuestDoc[]
): EffectiveOverrides => {
  const recordOverrides = new Map<string, EffectiveRecordOverride>()
  const mutes = new Map<string, EffectiveMute>()
  const owners = new Map<string, RecordOwner>()
  // The winning entry itself, kept beside the owner so the fields can be read
  // off it once the contest is settled.
  const winning = new Map<string, RecordOverridesEntry>()

  for (const quest of quests) {
    for (const [submissionId, entry] of Object.entries(
      quest.entity.recordOverrides ?? {}
    )) {
      const updatedAt = lastTouchedAt(entry)
      // An entry with no timestamp anywhere holds nothing: no field, no mute.
      if (!updatedAt) continue

      const incumbent = owners.get(submissionId)
      if (incumbent && !isNewer(updatedAt, incumbent.updatedAt)) continue

      owners.set(submissionId, { submissionId, questId: quest.id, updatedAt })
      winning.set(submissionId, entry)
    }
  }

  for (const [submissionId, owner] of owners) {
    const entry = winning.get(submissionId)
    if (!entry) continue

    // Read field by field rather than iterating the map, because each field's
    // stored shape is its own — see `recordFieldOverridesSchema`. Iterating gave
    // every entry the same type and needed a cast per field to pretend otherwise.
    const money = entry.fields?.annualValue
    if (money) {
      recordOverrides.set(
        effectiveRecordOverrideKey(submissionId, 'annualValue'),
        {
          submissionId,
          field: 'annualValue',
          value: money.value,
          baseValue: money.baseValue ?? null,
          questId: owner.questId,
          updatedAt: money.updatedAt,
        }
      )
    }

    const dateRange = entry.fields?.dateRange
    if (dateRange) {
      recordOverrides.set(
        effectiveRecordOverrideKey(submissionId, 'dateRange'),
        {
          submissionId,
          field: 'dateRange',
          value: dateRange.value,
          baseValue: dateRange.baseValue ?? null,
          questId: owner.questId,
          updatedAt: dateRange.updatedAt,
        }
      )
    }

    // Stored explicitly as on/off, so the owner turning a record back on is a
    // real entry rather than the absence of one.
    if (entry.muted?.value) {
      mutes.set(submissionId, {
        submissionId,
        questId: owner.questId,
        updatedAt: entry.muted.updatedAt,
      })
    }
  }

  return { recordOverrides, mutes, owners }
}

export default resolveEffectiveOverrides
